import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  initGA, 
  trackPageView, 
  getRealAnalyticsData,
  AnalyticsData,
  isGALoaded 
} from '../services/analytics';

// Hook for initializing and using Google Analytics
export const useAnalytics = () => {
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize GA on first load
    if (!isInitialized) {
      initGA();
      setIsInitialized(true);
    }
  }, [isInitialized]);

  useEffect(() => {
    // Track page views on route changes
    if (isInitialized && isGALoaded()) {
      trackPageView(location.pathname);
    }
  }, [location.pathname, isInitialized]);

  return {
    isInitialized: isInitialized && isGALoaded(),
    trackPageView,
  };
};

// Hook for fetching analytics data
export const useAnalyticsData = () => {
  // Initialize with default structure to prevent undefined errors
  const [data, setData] = useState<AnalyticsData | null>({
    dailyVisits: [],
    monthlyViewers: { total: 0, growth: 0 },
    topLocations: [],
    buyButtonStats: { 
      totalClicks: 0,
      monthlyClicks: 0,
      weeklyClicks: 0,
      topCategory: { name: 'No Data', clicks: 0 }
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Real-only: do not fall back to mock data
      const analyticsData = await getRealAnalyticsData();
      if (analyticsData) setData(analyticsData);
      // If null, keep defaults (zeros / empty) to indicate no real data yet
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
      console.error('Analytics data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Refresh data every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
};
